import { Context } from '../context/Context';
import { RenderTexture } from './RenderTarget';

export class GBuffer {
  albedoTexture!: RenderTexture;
  normalTexture!: RenderTexture;
  motionTexture!: RenderTexture;
  depthTexture!: RenderTexture;
  framebuffer!: WebGLFramebuffer;
  decalFramebuffer!: WebGLFramebuffer;

  width!: number;
  height!: number;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    // Create G-buffer framebuffer
    this.framebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);

    // COLOR_ATTACHMENT0: Albedo (RGBA8) — .rgb = albedo, .a = metallic
    this.albedoTexture = new RenderTexture();
    this.albedoTexture.glTexture = this.createColorTexture(
      gl,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.albedoTexture.glTexture,
      0,
    );

    // COLOR_ATTACHMENT1: World Normal (RGBA16F) — .xyz = normal, .w = roughness
    this.normalTexture = new RenderTexture();
    this.normalTexture.glTexture = this.createColorTexture(gl, gl.RGBA16F, gl.RGBA, gl.FLOAT);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT1,
      gl.TEXTURE_2D,
      this.normalTexture.glTexture,
      0,
    );

    // COLOR_ATTACHMENT2: Motion vectors (RG16F) — screen-space ΔUV from previous
    // frame to current frame. Used by TAA + future temporal passes to reproject
    // history samples. Encoded as (prevUV - currentUV); float16 has plenty of
    // range for sub-screen-width per-frame motion.
    this.motionTexture = new RenderTexture();
    this.motionTexture.glTexture = this.createColorTexture(
      gl,
      gl.RG16F,
      gl.RG,
      gl.HALF_FLOAT,
    );
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT2,
      gl.TEXTURE_2D,
      this.motionTexture.glTexture,
      0,
    );

    // DEPTH_ATTACHMENT: 24-bit depth as a sampleable texture. World position is
    // reconstructed from this + screen UV + invViewProjection in consumer shaders
    // (see _shared/world_pos_from_depth.glsl). Replaces a former RGBA32F position
    // attachment; depth is perspective-correct and avoids large-coord precision loss.
    this.depthTexture = new RenderTexture();
    this.depthTexture.glTexture = this.createDepthTexture(gl);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.TEXTURE_2D,
      this.depthTexture.glTexture,
      0,
    );

    // Enable MRT: albedo, normal, motion vectors.
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2]);

    // Validate
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`G-buffer framebuffer incomplete: ${status}`);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Decal FBO: shares albedo texture + depth texture.
    // Normal texture is NOT attached, so it can be read as a sampler without
    // causing a feedback loop. Decals sample the depth texture too — they read
    // before writing to albedo, so no feedback loop.
    this.decalFramebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.decalFramebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.albedoTexture.glTexture,
      0,
    );
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.TEXTURE_2D,
      this.depthTexture.glTexture,
      0,
    );
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

    const decalStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (decalStatus !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`Decal framebuffer incomplete: ${decalStatus}`);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private createColorTexture(
    gl: WebGL2RenderingContext,
    internalFormat: number,
    format: number,
    type: number,
  ): WebGLTexture {
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, this.width, this.height, 0, format, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  private createDepthTexture(gl: WebGL2RenderingContext): WebGLTexture {
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.DEPTH_COMPONENT24,
      this.width,
      this.height,
      0,
      gl.DEPTH_COMPONENT,
      gl.UNSIGNED_INT,
      null,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  bind(ctx: Context): void {
    const gl = ctx.gl;
    // Unbind any textures still bound from a previous pass before attaching
    // the G-buffer FBO. This prevents feedback loops where G-buffer textures
    // from the previous frame's light pass are still bound to texture units
    // while the G-buffer FBO attaches them for writing. textureState.clear()
    // physically unbinds every tracked unit.
    ctx.textureState.clear();

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.viewport(0, 0, this.width, this.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  }

  bindForDecals(gl: WebGL2RenderingContext): void {
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.decalFramebuffer);
    gl.viewport(0, 0, this.width, this.height);
    // Do NOT clear — we want to blend into the existing albedo
  }

  unbind(gl: WebGL2RenderingContext): void {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  unbindTextures(ctx: Context): void {
    // Clear the engine's texture state cache and physically unbind every
    // tracked unit. Without this, the G-buffer textures remain bound as
    // samplers from the light pass, creating a feedback loop when the G-buffer
    // FBO is bound for writing in the next geometry pass.
    ctx.textureState.clear();
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteFramebuffer(this.decalFramebuffer);
    if (this.albedoTexture.glTexture) gl.deleteTexture(this.albedoTexture.glTexture);
    if (this.normalTexture.glTexture) gl.deleteTexture(this.normalTexture.glTexture);
    if (this.motionTexture.glTexture) gl.deleteTexture(this.motionTexture.glTexture);
    if (this.depthTexture.glTexture) gl.deleteTexture(this.depthTexture.glTexture);
  }
}
